<template>
  <div class="ModifyInsuranceModal">
    <v-form
      ref="$form"
      validate-on="input lazy"
      :hidden="showHintView"
    >
      <ForWhomSelectorComponent
        :forWhom="newAnswers.forWhom"
        :forWhomPeople="newAnswers.forWhomPeople"
        @update:forWhom="updateForWhom"
        @update:forWhomPeople="updateForWhomPeople"
        :max-people="props.configurations.maxInsurances"
      />

      <div v-if="forWhom != ForWhomEnum.ME">
        <label for="has-relationship">
          {{ t('rater.dental.has-relationship.form-legend') }}
        </label>

        <RadioButtonGroup
          :id="'has-relationship'"
          :value="newAnswers.hasRelationship"
          :model-value="newAnswers.hasRelationship"
          :options="commonOptions"
          flex-direction="row"
          @update:modelValue="updateHasRelationship"
        />

        <label class="relationship-type-legend">
          {{ t('rater.dental.relationship.form-legend') }}
        </label>

        <RelationshipComponent
          v-model="insureds"
          :answers="newAnswers"
          :configurations="configurations"
          :externalInit="true"
          @update:modelValue="updateRelationships"
        />
      </div>

      <DatePickerComponent
        id="start-date"
        :label="t('rater.common.modify-search-view.start-date')"
        :modelValue="newAnswers.startDate ?? addDaysToDate(1)"
        @update:modelValue="updateStartDate"
        :datePickerVisible="!isModify"
        :min-date="minDateInit"
        :max-date="addDaysToDate(configurations.maxDays)"
        :rules="[required]"
      />

      <BirthDatesComponent
        v-model="insureds"
        :for-whom="newAnswers.forWhom!"
        :configurations="configurations"
        :start-date="newAnswers.startDate!"
        :relationships="props.configurations.relationships"
      />

      <!-- Warning de accesibilidad si se emplea <label> -->
      <p class="label">
        {{ hasInsuranceLabel }}
      </p>

      <RadioButtonGroup
        :value="newAnswers.hasHealthInsurance"
        :id="'has-health-insurance'"
        :options="commonOptions"
        flex-direction="row"
        @update:modelValue="updateHasInsurance"
      />

      <div class="actions">
        <BaseButton @click="checkForSubmit">
          {{ t('rater.base.' + (isModify ? 'recalculate-submit' : 'submit')) }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
      </div>
    </v-form>

    <HintComponent
      v-if="showHintView"
      :configurations="configurations"
      @action="checkOnlyForFamily"
    />
  </div>
</template>

<script setup lang="ts">
  import ForWhomSelectorComponent from '@/modules/core/components/formFields/ForWhomSelectorComponent.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import HintComponent from '@/modules/dental/rater/components/HintComponent.vue'
  import { computed, onBeforeMount, ref, shallowRef, toRaw } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { addDaysToDate } from '@/utils/date.utils'

  import BirthDatesComponent from '@/modules/common/common/components/panels/BirthDatesComponent.vue'
  import RelationshipComponent from '@/modules/common/common/components/panels/RelationshipComponent.vue'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { useToastStore } from '@/modules/core/stores/toastStore'
  import DentalConfigurations from '@/modules/dental/common/models/APP/DentalConfigurations'
  import { arrayRange } from '@/utils/array.utils'
  import DentalRaterAnswers from '../../models/APP/DentalRaterAnswers'

  interface PropsInterface {
    answers: DentalRaterAnswers
    configurations: DentalConfigurations
    isModify?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    isModify: true
  })

  const emit = defineEmits(['submit', 'close'])

  const { t } = useI18n()
  const { required } = useRules()
  const toastStore = useToastStore()

  const $form = ref()
  const newAnswers = ref(new DentalRaterAnswers(props.answers))
  newAnswers.value.startDate = props.answers.startDate ?? addDaysToDate(1)
  const insureds = shallowRef<Insured[]>(getLoadedInsureds())

  const minDateInit = computed(() => {
    const tomorrow = addDaysToDate(1, new Date())
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  })

  const forWhom = ref(newAnswers.value.forWhom)
  const showHintView = ref(false)
  let updatedForWhom: string | null | undefined = undefined

  const hasInsuranceLabel = computed(() => {
    if (newAnswers.value.forWhom === ForWhomEnum.ME) {
      return props.configurations.tags.hasHealthInsuranceMeTitle
    }

    if (newAnswers.value.forWhom === ForWhomEnum.ME_MORE) {
      return props.configurations.tags.hasHealthInsuranceMeAndOthersTitle
    }

    return props.configurations.tags.hasHealthInsuranceOthersTitle
  })

  const holderRelationship = computed(
    () => props.configurations.relationships.find((r) => r.holder)!.id
  )

  const commonOptions: OptionsValuesInterface[] = [
    {
      label: t('common.choices.yes'),
      value: 'yes'
    },
    {
      label: t('common.choices.no'),
      value: 'no'
    }
  ]

  function getLoadedInsureds(): Insured[] {
    const insureds = newAnswers.value.insureds as Insured[]
    return insureds.length ? insureds : [new Insured()]
  }

  async function checkForSubmit() {
    if (newAnswers.value.forWhom !== ForWhomEnum.ME && newAnswers.value.hasRelationship === 'no') {
      const { valid } = await $form.value.validate()
      if (!valid) return
      showHintView.value = true
    } else {
      await submit()
    }
  }

  function updateUserDetails() {
    newAnswers.value.insureds = insureds.value
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return

    updateUserDetails()

    emit('submit', newAnswers.value)
  }

  function getCleanInsureds(
    currentPosition = 1,
    length = props.answers.forWhomPeople,
    reset = false
  ) {
    return arrayRange(currentPosition, length).map((i) => {
      const isFirst = i === 1
      const isHolder = isFirst && props.answers.forWhom !== ForWhomEnum.NOT_ME
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
      const relationship = isHolder
        ? holderRelationship.value
        : insureds.value[i - 1]?.relationship !== holderRelationship.value // check previous relationship is not holder
        ? insureds.value[i - 1]?.relationship
        : ''
      const userDetails = new UserDetails()
      if (!reset) {
        userDetails.birthDate = insureds.value[i - 1]?.userDetails?.birthDate
      }
      return new Insured({
        userDetails,
        addressDetails: new AddressDetails({
          postalCode: insureds.value[i - 1]?.addressDetails?.postalCode
        }),
        isHolder,
        role,
        relationship,
        hasHealthPolicy: newAnswers.value.hasHealthInsurance
      })
    })
  }

  function updateHasInsurance(newValue: 'yes' | 'no') {
    newAnswers.value.hasHealthInsurance = newValue
    insureds.value.forEach((i) => {
      i.hasHealthPolicy = newValue
    })
    newAnswers.value.insureds = insureds.value
  }

  function updateHasRelationship(newValue: 'yes' | 'no') {
    newAnswers.value.hasRelationship = newValue
    if (newValue === 'no') {
      toastStore.warningToast(t('rater.dental.modify-insurance-modal.no-relationship-alert'), true)
    }
  }

  function updateStartDate(newValue: Date) {
    newAnswers.value.startDate = newValue
  }

  function updateForWhom(newValue: string) {
    if (forWhom.value !== newValue) {
      updatedForWhom = forWhom.value
      forWhom.value = newValue
      newAnswers.value.forWhom = newValue
    }
  }

  function updateForWhomPeople(newValue: number) {
    if (updatedForWhom) {
      if (
        forWhom.value !== ForWhomEnum.NOT_ME &&
        (!updatedForWhom || updatedForWhom !== ForWhomEnum.NOT_ME)
      ) {
        updateUserDetails()
        // keep holder and reset other insureds
        insureds.value = [toRaw(insureds.value[0]), ...getCleanInsureds(2, newValue, true)]
      } else {
        // reset all insureds
        insureds.value = getCleanInsureds(1, newValue, true)
      }
      updatedForWhom = undefined
    } else if (insureds.value.length !== newValue) {
      // modified people number
      if (insureds.value.length >= newValue) {
        // subtracted
        insureds.value = insureds.value.slice(0, newValue).map((i) => toRaw(i))
      } else {
        // added
        insureds.value = [
          ...insureds.value,
          ...getCleanInsureds(insureds.value.length + 1, newValue)
        ].map((i) => toRaw(i))
      }
    }

    if (newAnswers.value.forWhom === ForWhomEnum.NOT_ME) {
      if (insureds.value[0].relationship === holderRelationship.value) {
        // holder removed
        insureds.value[0].relationship = ''
        insureds.value[0].role = UserRoleEnum.INSURED
      }
    } else if (insureds.value[0].relationship !== holderRelationship.value) {
      // holder added
      insureds.value[0].relationship = holderRelationship.value
      insureds.value[0].role = UserRoleEnum.HOLDER_INSURED
    }
    newAnswers.value.insureds = insureds.value
    newAnswers.value.forWhomPeople = newValue
  }

  function updateRelationships(newValue: Insured[] | string) {
    if (Array.isArray(newValue)) {
      insureds.value.forEach((insured, i) => {
        insured.relationship = newValue[i].relationship
      })
    } else if (typeof newValue === 'string') {
      insureds.value.forEach((insured) => {
        insured.relationship = newValue
      })
    }
  }

  async function checkOnlyForFamily(onlyFamily: boolean) {
    if (onlyFamily) {
      showHintView.value = false
      updateHasRelationship('yes')
    } else {
      await submit()
    }
  }

  function beforeMount() {
    updateForWhomPeople(props.answers.insureds.length || 1)
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .ModifyInsuranceModal {
    width: $size-full;
    max-width: $size-375;
    margin: $spacing-l auto;

    .tariff-subtitle {
      color: var(--pricing-subtitle-color);
      font-size: $font-size-l;
      text-transform: uppercase;
      margin-bottom: $spacing-xs;

      @include breakpoint-md {
        text-align: left;
      }
    }

    label,
    .label {
      color: $color-neutral-dark;
    }

    .form-legend {
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-42;
      margin-bottom: $spacing-xl;
      text-align: left;
      text-wrap: balance;
    }

    .relationship-type-legend {
      padding-bottom: $spacing-xl;
      display: block;
    }

    .actions {
      margin-top: $spacing-2;
      padding-bottom: $spacing-2xl;
    }

    .start-date_form-field {
      margin-top: $spacing-2;
    }
  }
</style>
